//
// Copyright (c) 2011-2017 by Curalate, Inc.
//

import $ from "jquery";

import _ from "js/underscore-mixins";

(function() {
  const DEFAULT_COUNTS = [10, 25, 50, 100];

    // Return a new controller for ngTable which extends the existing (hidden) controller
    // This function takes the a function representing the original ngTableController function
  const ngTableExControllerFunc = function(originalControllerFunc) {
    return ['$injector', '$scope', '$timeout', 'TrackingService', function($injector, $scope, $timeout, TrackingService) {
            // Invoke an instance of the original ngTableController, making sure to provide the correct $scope
            // argument to the $injector
      const originalController = $injector.invoke(originalControllerFunc, this, { $scope });
      angular.extend(this, originalController);

            // Define "superSortBy" after we invoke the parent controller to ensure it is on $scope
      const superSortBy = $scope.sortBy;

            // Override default sortBy so KM/GA events are only recorded on user action
      $scope.sortBy = function(...args) {
        const currentSorting = $scope.params.$params.sorting;

        superSortBy.apply(this, args);

        const newSorting = $scope.params.$params.sorting;

        if (!angular.equals(currentSorting, newSorting)) {
          if ($scope.onSortChange && angular.isFunction($scope.onSortChange)) {
            $scope.onSortChange();
          }

          if ($scope.params.parameters().tableName) {
            const sortingString = _($scope.params.$params.sorting).map((value, key) => { return `${key}=${value}`; }).join(';');

            if ($scope.params.parameters().trackingV2) {
              TrackingService.recordEvent($scope.params.parameters().tableName, 'DataTable-sort', sortingString);
            } else {
              TrackingService.recordEvent('DataTable', 'sort', $scope.params.parameters().tableName, sortingString);
            }
          }
        }
      };

            // Watch for any changes in the underlying $params object and detect
            // changes in parameters manually. This keeps the added $watcher count to 1
      $scope.$watch('params.$params', (newVal, oldVal) => {
        if (!angular.equals(oldVal, newVal) && !_.isEmpty(oldVal)) {
          const filterChanged = !angular.equals(oldVal.filter, newVal.filter);
          const pageChanged = !angular.equals(oldVal.page, newVal.page);
          const tableName = $scope.params.parameters().tableName; // The presence of a table name indicates we want to track actions on the table

          if (filterChanged) {
            if ($scope.onFilterChange && angular.isFunction($scope.onFilterChange)) {
              $scope.onFilterChange();
            }

            if (tableName) {
              const filterString = _(newVal.filter).map((value, key) => { return `${key}=${value}`; }).join(';');

              if ($scope.params.parameters().trackingV2) {
                TrackingService.recordEvent(tableName, 'DataTable-filter', filterString);
              } else {
                TrackingService.recordEvent('DataTable', 'filter', tableName, filterString);
              }
            }
          }

          if (pageChanged) {
            if ($scope.onPageChange && angular.isFunction($scope.onPageChange)) {
              $scope.onPageChange();
            }

            if (tableName) {
              if ($scope.params.parameters().trackingV2) {
                TrackingService.recordEvent(tableName, 'DataTable-page', tableName, newVal.page);
              } else {
                TrackingService.recordEvent('DataTable', 'page', tableName, newVal.page);
              }
            }
          }
        }
      }, true); // object equality
    }];
  };

    /**
     * ngTableEx
     *
     * Decorates ng-table in order to provide default functionality for all tables in the dashboard
     * This approach is necessary due to the fact that, by default, ng-table's controller is hidden to the "outside world"
     * The decorator approach injects a new controller which extends upon the "hidden" ng-table controller
     *
     * This is the place to add new default functionality that affects all tables on the dashboard, as the ngTable directive's
     * attributes can be accessed directly.
     */
  angular.module('curalate')
        .config(['$provide', function($provide) {
          $provide.decorator('ngTablePaginationDirective', ['$delegate', function($delegate) {
            const directive = $delegate[0];

            const originalLinkFunction = directive.link;

            directive.compile = function() {
              const injectorFunc = angular.injector(['ng']);
              const $parse = injectorFunc.get('$parse');

              return function(...args) {
                const scope = args[0];
                const attrs = args[2];
                if (attrs.counts) {
                  scope.$counts = $parse(attrs.counts)(scope);
                } else {
                  scope.$counts = DEFAULT_COUNTS;
                }

                originalLinkFunction.apply(this, args);

                scope.params.settings().$scope.$on('ngTableAfterReloadData', () => {
                  const count = Math.max(1, scope.params.count());
                  scope.numPages = Math.ceil(scope.params.total() / count);
                  scope.counts = scope.$counts;
                });
              };
            };

            return $delegate;
          }]);

          $provide.decorator('ngTableDirective', ['$delegate', function($delegate) {
            const directive = $delegate[0];

            directive.controller = ngTableExControllerFunc(directive.controller);

            const superCompileFunc = directive.compile;

            directive.compile = function(element, rawAttrs) {
              const injectorFunc = angular.injector(['ng', 'ngTable']);
              const $parse = injectorFunc.get('$parse');
              const superLinkFunc = superCompileFunc(element);
              const extendedColumns = [];
              let row;
              let numColumns;
              let emptyTableCell;
              let rowTemplate;

                    // Extend the native ng-table compile function to add additionally abilities to columns
              angular.forEach(element.find('tr'), (tr) => {
                tr = angular.element(tr);
                if (!tr.hasClass('ng-table-group') && !row) {
                  row = tr;
                }
              });

                    // This is only relevant if we're using a cell-based table.
              if (row) {
                angular.forEach(row.find('td'), (item) => {
                  const el = angular.element(item);
                  extendedColumns.push({
                    hide(scope) { return el.attr('ng-hide') ? $parse(el.attr('ng-hide'))(scope) : true; }, // requires custom template
                  });
                });
              }

                    // Add functionality for empty table state
                    // Create the single cell that will contain the empty text, but wait to insert the text below
                    // so we can allow Angular expressions to be evaluated for empty text (ex. multiple empty texts based on app state)
                    // TODO refactor this into a directive
              if (rawAttrs.emptyText) {
                if (row) {
                  numColumns = row.find('td').length;
                  row.attr('ng-if', '$data.length > 0');
                  emptyTableCell = $('<td class="empty-table-cell"></td>').attr('colspan', numColumns);
                  rowTemplate = $('<tr></tr>').attr('ng-if', '$data.length === 0').append(emptyTableCell);
                  row.before(rowTemplate);
                } else {
                            // Append empty text before pagination if we're not using a cell-based table.
                  const pagination = element.find('[ng-table-pagination]');
                  emptyTableCell = $('<span />').addClass('empty-table-cell');
                  const template = $('<div />').addClass('empty-text').attr('ng-if', '!$loading && $data.length === 0').append(emptyTableCell);
                  pagination.before(template);
                }
              }

              return function(scope, elem, attrs) {
                        // The ngTable compile function would have quit early if we're not using a cell-based table
                        // and would not have produced a link function
                if (superLinkFunc) {
                  superLinkFunc(scope, elem, attrs);
                } else {
                            // In the event we're not using a cell-based table, we need to manually attach the listener for
                            // params so the ngTable directive has the correct parameters on its scope
                  scope.$watch(attrs.ngTable, ((params) => {
                    if (angular.isUndefined(params)) {
                      return;
                    }
                    scope.paramsModel = $parse(attrs.ngTable);
                    scope.params = params;
                  }), true);
                }

                        // TODO omg this is terrible. please please fix me
                        // There must be a saner way of setting empty text via ng-table
                if (emptyTableCell) {
                  scope.$watch(attrs.emptyText, (newVal, oldVal) => {
                    if (newVal !== oldVal) {
                      elem.find('.empty-table-cell').text(newVal);
                    }
                  }, true);

                  emptyTableCell.text($parse(attrs.emptyText)(scope));
                }

                        // merge the ng-table default columns with extended columns
                angular.forEach(scope.$columns, (column, idx) => {
                  angular.extend(column, extendedColumns[idx]);
                });
              };
            };

            return $delegate;
          }]);
        }]);
})();
